import {debounce} from 'lodash';
import {useEffect, useMemo, useState} from 'react';

import {useRunSelectorFilteredRunExistsQuery} from '../../../generated/graphql';
import {type CustomRunNames} from '../../../state/views/customRunNames/types';
import {propagateErrorsContext} from '../../../util/errors';
import * as Filter from '../../../util/filters';
import type * as FilterTypes from '../../../util/filterTypes';
import {emptyQueryFilter} from '../../../util/filterV2';
import {isNetworkRequestSettled} from '../../../util/isNetworkRequestSettled';
import {addSearchFilter} from '../../../util/queryts';
import type * as RunTypes from '../../../util/runTypes';
import {type RunSetConfig} from '../../../util/section';
import * as SelectionManager from '../../../util/selectionmanager';

const DEBOUNCE_MS = 400;

export type UseSearchResultsHiddenByFiltersInputs = {
  enabled: boolean;
  userFilters: FilterTypes.Filter<RunTypes.Key>;
  mergeFilters: FilterTypes.Filter<RunTypes.Key> | undefined;
  groupSelections: SelectionManager.GroupSelectionState;
  onlyShowSelected: boolean;
  search: RunSetConfig['search'];
  customRunNames: CustomRunNames | undefined;
  projectInternalId: string | undefined;
};

export type UseSearchResultsHiddenByFiltersResult = {
  hasHiddenResults: boolean;
  loading: boolean;
  enabled: boolean;
};

type SearchResultsHiddenByFiltersQueryFilterInputs = Omit<
  UseSearchResultsHiddenByFiltersInputs,
  'enabled' | 'projectInternalId'
>;

export function getSearchResultsHiddenByFiltersQueryFilter(
  inputs: SearchResultsHiddenByFiltersQueryFilterInputs
): string | null {
  const {
    userFilters,
    mergeFilters,
    groupSelections,
    onlyShowSelected,
    search,
    customRunNames,
  } = inputs;

  // if there is no search query, we don't need to run the query.
  // we only care about "hidden by filter" if there is a search query.
  const hasSearch = (search?.query ?? '').trim() !== '';
  if (!hasSearch) {
    return null;
  }

  const hasUserFilters = Filter.countIndividual(userFilters) > 0;
  const selectedOnlyFilter = onlyShowSelected
    ? SelectionManager.toRunFilter(groupSelections)
    : null;
  const hasSelectedOnlyConstraint =
    selectedOnlyFilter != null && Filter.simplify(selectedOnlyFilter) !== true;
  const hasVisibleConstraints = hasUserFilters || hasSelectedOnlyConstraint;

  if (!hasVisibleConstraints) {
    return null;
  }

  const searchAndMergeFilters = addSearchFilter(
    Filter.mergedFilters(emptyQueryFilter(), mergeFilters),
    search,
    groupSelections.grouping,
    customRunNames
  );

  const visibleConstraintsFilter =
    hasSelectedOnlyConstraint && selectedOnlyFilter != null
      ? Filter.And([userFilters, selectedOnlyFilter])
      : userFilters;

  return JSON.stringify({
    $and: [
      Filter.toMongo(searchAndMergeFilters),
      // negate our visible constraints to get results against the filter.
      {$nor: [Filter.toMongo(visibleConstraintsFilter)]},
    ],
  });
}

export function useSearchResultsHiddenByFilters(
  inputs: UseSearchResultsHiddenByFiltersInputs
): UseSearchResultsHiddenByFiltersResult {
  const {
    enabled: flagEnabled,
    userFilters,
    mergeFilters,
    groupSelections,
    onlyShowSelected,
    search,
    customRunNames,
    projectInternalId,
  } = inputs;

  const currentFilterJSON = useMemo(() => {
    if (!flagEnabled) {
      return null;
    }
    return getSearchResultsHiddenByFiltersQueryFilter({
      userFilters,
      mergeFilters,
      groupSelections,
      onlyShowSelected,
      search,
      customRunNames,
    });
  }, [
    flagEnabled,
    userFilters,
    mergeFilters,
    groupSelections,
    onlyShowSelected,
    search,
    customRunNames,
  ]);

  const enabled =
    flagEnabled && projectInternalId != null && currentFilterJSON != null;
  const [debouncedFilterJSON, setDebouncedFilterJSON] = useState<string | null>(
    currentFilterJSON
  );
  const debouncedSet = useMemo(
    () =>
      debounce((value: string | null) => {
        setDebouncedFilterJSON(value);
      }, DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    debouncedSet(currentFilterJSON);
    return () => {
      debouncedSet.cancel();
    };
  }, [currentFilterJSON, debouncedSet]);

  const isStale = currentFilterJSON !== debouncedFilterJSON;
  const {
    data,
    loading: apolloLoading,
    error,
    networkStatus,
  } = useRunSelectorFilteredRunExistsQuery({
    skip:
      !enabled ||
      isStale ||
      debouncedFilterJSON == null ||
      projectInternalId == null,
    variables:
      enabled &&
      !isStale &&
      debouncedFilterJSON != null &&
      projectInternalId != null
        ? {projectInternalId, filters: debouncedFilterJSON}
        : undefined,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    context: propagateErrorsContext(),
  });

  if (!enabled) {
    return {
      hasHiddenResults: false,
      loading: false,
      enabled: false,
    };
  }

  if (isStale) {
    return {hasHiddenResults: false, loading: true, enabled: true};
  }

  if (error != null) {
    return {hasHiddenResults: false, loading: false, enabled: true};
  }

  const loading = apolloLoading || !isNetworkRequestSettled(networkStatus);

  if (loading || data?.project?.runs == null) {
    return {hasHiddenResults: false, loading: true, enabled: true};
  }

  return {
    hasHiddenResults: data.project.runs.edges.length > 0,
    loading: false,
    enabled: true,
  };
}
